import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { TraffickingTab } from '../../../../../../../../../apis/graphql';
import { DEBOUNCE_DURATION_MS } from '../../../../../../../../../common/hooks/constants';
import { TraffickingTableName } from '../../../../../../../../../constants';
import { useFiltersContext } from '../../../../../../../../../contexts/FilterContext';
import { getStringOperatorFilterKeys } from '../../../../../../../../../contexts/FilterContext/hooks/utils';
import { useGetTraffickingFilterConfig } from '../../../../../../../TableFilterBar/AdditionalFilters/hooks';
import {
  generateQueryVariables,
  getAllowedFilterKeys,
  getEffectiveDateFilter,
  getSelectedRowIdsFromState,
} from '../../../../../../../utils';
import { useDuplicateAdsStepperContext } from '../../../../../DuplicateAdsStepperProvider/DuplicateAdsStepperProvider';
import type { DuplicateLineItem } from '../../../../../DuplicateAdsStepperProvider/types';
import { LINE_ITEMS_LIMIT } from '../../constants';
import useFetchDuplicateLineItems from '../../hooks/useFetchDuplicateLineItems/useFetchDuplicateLineItems';

type UseSearchedData = {
  isSearching: boolean;
  searchResults: DuplicateLineItem[];
  onNext: () => Promise<void>;
  hasMore: boolean;
};

type Props = {
  searchValue: string;
};

export const useSearchedData = ({ searchValue }: Props): UseSearchedData => {
  const { adType } = useDuplicateAdsStepperContext();

  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<DuplicateLineItem[]>([]);

  const { filters, selectedCustomFilter } = useFiltersContext();
  const { filterConfigs } = useGetTraffickingFilterConfig();

  const lineItemAllowedKeys = useMemo(() => getAllowedFilterKeys(TraffickingTab.LineItem, filterConfigs), [
    filterConfigs,
  ]);
  const stringOperatorKeys = useMemo(() => getStringOperatorFilterKeys(filterConfigs), [filterConfigs]);

  const lineItemQueryVariables = useMemo(
    () =>
      generateQueryVariables(
        {
          ...filters,
          // When the V2 filter panel is on, the legacy Date shortcut is hidden and the panel has no
          // date/schedule UI, so the default "Running This Month" date filter must not leak into this
          // line-item search as a phantom schedule filter (KATANA-1494 follow-up).
          dateFilter: getEffectiveDateFilter(filters.dateFilter),
          searchTerm: '',
          sortOption: undefined,
          tableName: TraffickingTableName.lineItems,
          selectedRowIds: getSelectedRowIdsFromState(filters.lineItems?.selectedRowIds || []),
          campaignRowsIds: getSelectedRowIdsFromState(filters.campaigns?.selectedRowIds || []),
          selectedCustomFilterId: selectedCustomFilter?.id ?? null,
        },
        undefined,
        undefined,
        lineItemAllowedKeys ?? undefined,
        stringOperatorKeys
      ),
    [filters, selectedCustomFilter?.id, lineItemAllowedKeys, stringOperatorKeys]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    adProductList: _omittedAdProductList,
    ...lineItemFilterOptionsWithoutAdProductList
  } = lineItemQueryVariables.filterOptions;

  const { handleSearch: handleSearchDuplicateLineItems, hasMore } = useFetchDuplicateLineItems(
    {
      filterOptions: {
        ...lineItemFilterOptionsWithoutAdProductList,
        adPotentialTypeList: adType ? [adType.key] : [],
        skipCPEligibilityCalculation: true,
      },
      limit: LINE_ITEMS_LIMIT,
      offset: 0,
    },
    searchValue,
    true
  );

  const handleSearch = useCallback(
    async (newDebounceSearchValue: string): Promise<void | null> => {
      try {
        const transformedData = await handleSearchDuplicateLineItems(newDebounceSearchValue);
        setSearchResults(transformedData);
      } catch {
        return null;
      } finally {
        setIsSearching(false);
      }
    },
    [handleSearchDuplicateLineItems]
  );

  const onNext = async (): Promise<void> => {
    if (hasMore) {
      const transformedData = await handleSearchDuplicateLineItems(searchValue);
      setSearchResults((prevData) => [...prevData, ...transformedData]);
    }
  };

  useEffect(() => {
    setIsSearching(true);
    const debounceSearch = debounce(handleSearch, DEBOUNCE_DURATION_MS);
    debounceSearch(searchValue);

    return debounceSearch.cancel;
    // This hook should run only when searchValue is changed
    // handleSearch in dependency array leads to more fetches than it needs to have
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  return {
    onNext,
    isSearching,
    searchResults,
    hasMore,
  };
};
